import {fromEvent, merge, of, Subscription, throwError} from 'rxjs';

import {catchError, debounceTime, first, map, mergeMap, take, timeout} from 'rxjs/operators';
import {get, findIndex} from 'lodash';
import {ShapeSpawnerService} from '../shapes/shape-spawner.service';
import {Injectable} from '@angular/core';
import {EditorRegistryService} from '../mx-graph/editor/editor-registry.service';
import {CANVAS_TOP_MARGIN} from 'src/app/editor-interface/toolbar/util/constants';
import {ConfigService} from '@q9elements/ui-kit/common';
import {DiagramMetaStoreService} from '../../diagram-data/diagram-meta-store.service';
import {DiagramMetaService} from '../../diagram-data/diagram-meta.service';
import {environment} from '../../../environments/environment';
import {DiagramTypeConfigService} from '../../core/services/diagram-type-config.service';

declare let mxCell: any;
declare let mxEvent: any;

@Injectable()
export class DiagramCanvasService {
  graph;
  Editor;
  EditorUI;
  renderActive: any;
  swimlanesVisible = true;
  stickersVisible = true;
  alreadyDrownId: string;

  constructor(
    private spawner: ShapeSpawnerService,
    private registry: EditorRegistryService,
    private diagramMetaStoreService: DiagramMetaStoreService,
    private diagramMetaService: DiagramMetaService,
    private diagramTypeConfigService: DiagramTypeConfigService
  ) {}

  setMain(graph, Editor, EditorUI) {
    this.graph = graph;
    this.Editor = Editor;
    this.EditorUI = EditorUI;
  }

  getGraph() {
    return this.graph;
  }

  drawFromJson(json): Promise<void> {
    this.renderActive = true;
    this.EditorUI.clearDefaultStyle();

    const isArchitecture = this.diagramTypeConfigService.isArchitecture();

    this.spawner.setArchitecture(isArchitecture);

    const layerMap = this.createRootLayers();

    layerMap.getLayer(8).root.setStyle(this.getSwimlaneLayerStyle(json));

    const resources: Set<string> = new Set();

    if (json.swimlines && json.swimlines.lines) {
      const layer = layerMap.getLayer(8);

      json.swimlines.lines.forEach(swimline =>
        layer.addChild(this.spawner.createSwimlane(swimline, layer.root))
      );
    }

    if (json.activities) {
      const layer = layerMap.getLayer(2);

      json.activities.forEach(activity => {
        get(activity, 'resources', []).forEach(resource => resources.add(resource.icon));
        layer.addChild(this.spawner.createActivity(activity, layer.root));
      });
    }

    if (json.flowlines) {
      const flLayer = layerMap.getLayer(3);
      const connLayer = layerMap.getLayer(9);
      const actLayer = layerMap.getLayer(2);

      json.flowlines.forEach(flowline => {
        if (flowline.connector) {
          connLayer.addChild(
            this.spawner.createConnector(flowline.connector, flowline.mxKey, connLayer.root)
          );
        } else if (flowline.terminator) {
          connLayer.addChild(this.spawner.createTerminator(flowline.terminator, connLayer.root));
        }

        let source = null;
        let target = null;

        if (flowline.srcId) {
          source =
            connLayer.getChild(Number(flowline.srcId)) || actLayer.getChild(Number(flowline.srcId));
        }

        if (flowline.tgtId) {
          target =
            connLayer.getChild(Number(flowline.tgtId)) || actLayer.getChild(Number(flowline.tgtId));
        }

        const edge = this.spawner.createFlowline(flowline, true, source, target, flLayer.root);

        if (isArchitecture) {
          // check if there is an interaction for this line
          const iaIndex = findIndex(
            json.interactions,
            (ia: any) => ia.flowlineMxKey === flowline.mxKey
          );

          if (iaIndex > -1) {
            const interaction = json.interactions[iaIndex];
            const cell = this.spawner.createInteractionCell(interaction, iaIndex + 1, flowline);

            edge.insert(cell);
          }
        }

        flLayer.addChild(edge);
      });
    }

    if (json.texts) {
      const layer = layerMap.getLayer(4);

      json.texts.forEach(text => {
        layer.addChild(this.spawner.createText(text, layer.root));
      });
    }

    if (json.notes) {
      const layer = layerMap.getLayer(7);

      json.notes.forEach(sticker => {
        layer.addChild(this.spawner.createSticker(sticker, layer.root));
      });
    }

    const buildLayer = images => {
      if (images) {
        const layer = layerMap.getLayer(5);

        images.forEach(image => {
          if (image.deleted) {
            return;
          }

          layer.addChild(this.spawner.createImage(image, image.imageResized, layer.root));
        });
      }
    };

    return Promise.all([
      this.preloadImages(json.images || []).then(buildLayer),
      this.preloadResourceIcons(Array.from(resources))
    ]).then(() => {
      const layers = layerMap.toArray();
      const mainParent = layers.shift().root;
      const root = new mxCell();
      // cache selected cells data (actually sticky notes in view mode)
      const selectedCellIds = this.graph.getSelectionCells().map(cell => cell.id);

      layers.forEach(layer => mainParent.insert(layer.root));
      root.insert(mainParent);
      this.graph.model.setRoot(root);
      this.graph.layoutManager.layoutCells([this.graph.model.getCell(8)]);
      this.Editor.setModified();
      this.graph.setSelectionCells(selectedCellIds.map(id => this.graph.model.getCell(id)));

      if (String(this.alreadyDrownId) !== String(json.id)) {
        this.graph.clearSelection();
        this.zoomToFitIfNeeded(this.diagramTypeConfigService.generalSettings().zoomToFitNeeded);
        this.EditorUI.resetScrollbars();
        this.restoreViewportPosition();
      }

      this.alreadyDrownId = json.id;
      this.renderActive = false;
    });
  }

  redrawActivity(mxKey): void {
    this.registry.getGraph().model.getCell(Number(mxKey))?.comp.instance.setupFromJson();
  }

  private createRootLayers(): LayerMap {
    const makeCell = id => {
      const cell = new mxCell();

      cell.setId(id);

      return cell;
    };

    const subRoot = makeCell(1);
    const activityLayer = makeCell(2);
    const flowlineLayer = makeCell(3);
    const textLayer = makeCell(4);
    const imageLayer = makeCell(5);
    const backgroundLayer = makeCell(6);
    const stickerLayer = makeCell(7);

    stickerLayer.setVisible(this.stickersVisible);
    const swimlaneLayer = makeCell(8);

    swimlaneLayer.setVisible(this.swimlanesVisible);
    const connectorLayer = makeCell(9);
    /**
     *    '<root>',
     '<mxCell id="0"/>',
     '<mxCell id="1" parent="0"/>',
     '<mxCell id="6" parent="1"/>', // background
     '<mxCell id="8" parent="1" style="childLayout=stackLayout;'
     + this.getSwimlaneLayerStyle(json) + ';resizeParent=1;"
     visible="' + visiblePool + '"/>', // swimlanes
     '<mxCell id="5" parent="1"/>', // images
     '<mxCell id="4" parent="1"/>', // text
     '<mxCell id="3" parent="1"/>', // flowline
     '<mxCell id="9" parent="1"/>', // connector, terminator
     '<mxCell id="2" parent="1"/>',  // activity
     '<mxCell id="7" parent="1" visible="' + visibleStickers + '"/>' // sticker
     */

    /** Layers will be prioritized in order of array if priority isn't set implicitly**/
    return new LayerMap([
      new Layer(subRoot),
      new Layer(backgroundLayer),
      new Layer(swimlaneLayer),
      new Layer(imageLayer),
      new Layer(textLayer),
      new Layer(activityLayer),
      new Layer(connectorLayer),
      new Layer(flowlineLayer),
      new Layer(stickerLayer)
    ]);
  }

  private getSwimlaneLayerStyle(json: any) {
    const poolExists = !!json.swimlines;
    const result = [
      'childLayout=stackLayout',
      'horizontalStack=' + (poolExists ? json.swimlines.horizontal : 0),
      'startX=' + (poolExists ? json.swimlines.position.x : 0),
      'startY=' + (poolExists ? json.swimlines.position.y : 0),
      'resizeParent=1'
    ];

    return result.join(';');
  }

  private preloadResourceIcons(icons: string[]) {
    const mapper = (icon: string) => this._preloadImage({src: icon, type: 'Icon'});

    return Promise.all(icons.map(mapper));
  }

  private _preloadImageWithAlt(src: string): Promise<string> {
    const altSrc = `${ConfigService.environment.STATIC_FILES_URL}/icons/no-image.png`;

    if (!src) {
      return of(altSrc).toPromise();
    }

    return this._preloadImage({src, type: 'Image', altSrc});
  }

  private _preloadImage({
    src,
    type,
    altSrc = null
  }: {
    src: string;
    type: string;
    altSrc?: string;
  }): Promise<null | string> {
    const vImg = new Image();

    const errorHandle = () => throwError(() => new Error(`${type} loading timeout`));
    const error = fromEvent(vImg, 'error').pipe(
      mergeMap(() => throwError(() => new Error(`${type} error`)))
    );
    const abort = fromEvent(vImg, 'abort').pipe(
      mergeMap(() => throwError(() => new Error(`${type} loading abort`)))
    );
    const loaded = fromEvent(vImg, 'load').pipe(
      map(e => src),
      timeout(6000),
      catchError(errorHandle)
    );

    if (!src) {
      return of(altSrc || null).toPromise();
    }

    vImg.src = src;

    return merge(error, abort, loaded)
      .pipe(
        catchError(e => of(altSrc)),
        first()
      )
      .toPromise();
  }

  private preloadImages(images): any {
    return Promise.all(
      images.map(image =>
        this._preloadImageWithAlt(image.deleted ? '' : image.imageResized).then(res => {
          image.imageResized = res;

          return image;
        })
      )
    );
  }

  private zoomToFitIfNeeded(sfa = false): Subscription | void {
    this.graph.zoomTo(1);

    const graphBound = this.graph.view.getGraphBounds();
    const browserWidth = window.innerWidth;
    const browserHeight = window.innerHeight;
    const canvasHeight = this.graph.container.clientHeight - CANVAS_TOP_MARGIN(sfa);

    // if diagram is in hidden iframe => setup subscriber on window resize
    // so when it become visible - resize to fit
    if (!browserHeight || !browserWidth) {
      return fromEvent(window, 'resize')
        .pipe(debounceTime(500), take(1))
        .subscribe(() => this.graph.fit());
    }

    // if content has bigger width/height than user viewport we should adjust to fit
    if (browserWidth < graphBound.width || canvasHeight < graphBound.height) {
      this.graph.fit(this.graph.getBorder(), false, CANVAS_TOP_MARGIN(sfa));

      return;
    }

    // no content
    if (graphBound.width === 0 || graphBound.height === 0) {
      return;
    }
    // if (graphBound.width <= 680 && graphBound.height <= 445) {
    // if (graphBound.width <= browserWidth && graphBound.height <= browserHeight) {
    // this.graph.zoomTo(1);
    // }
  }

  public saveViewportPosition(diagramJson: any): void {
    if (environment.NOUI) {
      return;
    }

    const {diagramId, state} = diagramJson;
    const {scale, translate} = this.graph.view;
    const viewportX = this.graph.container.scrollLeft / scale - translate.x;
    const viewportY = this.graph.container.scrollTop / scale - translate.y;
    const viewport = {scale, viewportX, viewportY};

    this.diagramMetaStoreService.updateDiagram(diagramId, state, {viewport});
  }

  public restoreViewportPosition(): void {
    if (environment.NOUI) {
      return;
    }

    const {translate} = this.graph.view;
    const {viewport} = this.diagramMetaService.getMetaSync();

    if (!viewport) {
      return;
    }

    const {scale, viewportX, viewportY} = viewport;

    this.graph.view.setScale(scale);
    this.graph.container.scrollLeft = (viewportX + translate.x) * scale;
    this.graph.container.scrollTop = (viewportY + translate.y) * scale;
  }
}

class Layer {
  /** parent MxCell **/
  public readonly root: any;
  public priority: number;

  private childMap: {
    [index: number]: any;
  };

  constructor(root, assemblyOrder?: number) {
    this.childMap = {};
    this.root = root;

    if (assemblyOrder !== undefined) {
      this.priority = assemblyOrder;
    }
  }

  removeChild(cellId) {
    const spaceHolderCell = this.root.children.find(cell => Number(cell.id) === Number(cellId));

    if (!spaceHolderCell) {
      throw new Error('Cell does not exist!');
    }

    delete this.childMap[cellId];

    return spaceHolderCell;
  }

  addChild(cell) {
    const key = Number(cell.id);

    if (this.childMap[key]) {
      throw new Error('Duplicate mxKey');
    }

    // this.children.push(cell);
    this.root.insert(cell);
    /** inserts cell into parent layer cell **/
    this.childMap[key] = cell;
  }

  getChild(key: number) {
    return this.childMap[key];
  }
}

class LayerMap {
  private struct: {[layerKey: number]: Layer};

  constructor(data?: Array<Layer>) {
    this.struct = {};

    if (data) {
      let initPriority = 0;

      data.forEach(layer => {
        if (layer.priority === undefined) {
          layer.priority = initPriority++;
        }

        this.addLayer(layer);
      });
    }
  }

  addLayer(layer: Layer) {
    const index = Number(layer.root.id);

    if (Number.isNaN(index)) {
      throw new Error('Layer root id is not a number');
    }

    if (this.struct[index]) {
      throw new Error('Duplicate layer name');
    }

    this.struct[index] = layer;
  }

  getLayer(key: number /**layer root mxKey**/) {
    return this.struct[key];
  }

  /** returns all layers in the correct order of render **/
  toArray(): Array<Layer> {
    return Object.keys(this.struct)
      .map(key => this.struct[key])
      .sort((curr: Layer, next: Layer) => {
        if (curr.priority < next.priority) {
          return -1;
        }

        if (curr.priority > next.priority) {
          return 1;
        }

        return 0;
      });
  }
}
